/**
 * AGC: New Simulation Battle Component - Exact copy of PVEBattle design
 * AGC: Simulation mode - always shows 5 buffs of each type and 1 potion of each type
 * AGC: No blockchain interaction, pure backend simulation
 */

import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Socket } from 'socket.io-client'
import { usePlayerProgression } from '../hooks/usePlayerProgression'
import { HybridPvEBattle } from './HybridPvEBattle'
import { playSoundSafe, unlockAudioPlayback } from '../utils/audio'
import { BuffSelector } from './BuffSelector'
import { DifficultySelector, Difficulty } from './DifficultySelector'
import { useModal } from '../hooks/useModal'
import { BattleResultModal } from './modals/BattleResultModal'
import { useWebSocket } from '../hooks/useWebSocket'
import { Info, HelpCircle } from 'lucide-react'
import { useTourGuide } from '../providers/TourGuideProvider'

interface NewSimulationProps {
  playerAddress: string
  socket?: Socket  // AGC: Optional - component uses its own useWebSocket() hook
  onBackToMenu: () => void
}

export const NewSimulation: React.FC<NewSimulationProps> = ({
  playerAddress,
  socket: _socket,  // AGC: Unused - component uses its own useWebSocket() hook
  onBackToMenu
}) => {
  const { playerStats } = usePlayerProgression(playerAddress)
  const { socket: wsSocket, isConnected } = useWebSocket()
  const { alert, Modals } = useModal()
  const { startTour } = useTourGuide()

  // AGC: Selection state
  const [difficulty, setDifficulty] = useState<Difficulty>('normal')
  const [selectedBuffs, setSelectedBuffs] = useState<number[]>([])
  const [selectedPotion, setSelectedPotion] = useState<number | null>(null)
  const [showBattle, setShowBattle] = useState(false)
  const [battleId, setBattleId] = useState<string>('')
  const [botLevel, setBotLevel] = useState<number>(1)
  const [isBattling, setIsBattling] = useState(false)
  
  // AGC: Battle result modal state
  const [showResultModal, setShowResultModal] = useState(false)
  const [battleResult, setBattleResult] = useState<{
    won: boolean
    isDraw: boolean
  }>({ won: false, isDraw: false })

  const startBattleButtonRef = useRef<HTMLButtonElement | null>(null)
  const [highlightStartButton, setHighlightStartButton] = useState(false)
  const hasDifficultyInteractionRef = useRef(false)
  const hasBuffSelectionInteractionRef = useRef(false)

  // AGC: Difficulty levels for simulation
  const difficultyLevels = {
    normal: { level: 3, name: 'Easy', description: 'Easy bot, good for learning' },
    expert: { level: 5, name: 'Expert', description: 'Challenging bot, requires strategy' },
    insane: { level: 15, name: 'Insane', description: 'Extreme difficulty, for masters only' }
  }

  // AGC: Scroll to top when component mounts
  useEffect(() => {
    console.log('🎮 [NewSimulation] Component mounted, playerAddress:', playerAddress)
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [playerAddress])

  // AGC: Remove highlight after delay
  useEffect(() => {
    if (!highlightStartButton) return
    const timeoutId = setTimeout(() => setHighlightStartButton(false), 3000)
    return () => clearTimeout(timeoutId)
  }, [highlightStartButton])

  // AGC: Helper to focus mobile button
  const focusMobileButton = (
    buttonRef: React.RefObject<HTMLButtonElement | null>,
    setHighlight: (value: boolean) => void,
  ) => {
    const button = buttonRef.current
    if (!button) return

    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      button.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }

    try {
      button.focus({ preventScroll: true })
    } catch (error) {
      button.focus()
    }
    setHighlight(true)
  }

  const handleDifficultySelect = (value: Difficulty) => {
    unlockAudioPlayback()
    setDifficulty(value)
    hasDifficultyInteractionRef.current = true
    focusMobileButton(startBattleButtonRef, setHighlightStartButton)
  }

  const handleBuffSelection = (combinedItems: number[]) => {
    unlockAudioPlayback()
    
    // AGC: Separate buffs (0-3) from potions (10000+)
    // AGC: BuffSelector uses offset 10000 for potions to distinguish from buffs
    const buffs: number[] = []
    const potions: number[] = []
    
    for (const item of combinedItems) {
      if (item >= 10000) {
        // AGC: Potion tokenId is offset by 10000, so subtract to get actual tokenId
        potions.push(item - 10000)
      } else {
        buffs.push(item)
      }
    }
    
    setSelectedBuffs(buffs)
    
    // AGC: Set selected potion from BuffSelector's POTIONS tab
    if (potions.length > 0) {
      setSelectedPotion(potions[0])
      console.log('⚗️ Potion selected from BuffSelector:', potions[0])
    } else {
      setSelectedPotion(null)
    }

    hasBuffSelectionInteractionRef.current = true
    focusMobileButton(startBattleButtonRef, setHighlightStartButton)
  }

  const handleStartBattle = async () => {
    if (selectedBuffs.length === 0) {
      await alert('Please select at least one buff NFT for the simulation!', { type: 'warning' })
      return
    }
    
    unlockAudioPlayback()
    
    // AGC: Generate unique battle ID for simulation
    const newBattleId = `sim-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    setBattleId(newBattleId)
    setBotLevel(difficultyLevels[difficulty].level)
    setIsBattling(true)
    setShowBattle(true)
  }

  const handleBattleEnd = (won: boolean, turns: number, isDraw: boolean = false) => {
    console.log(`🎮 Simulation battle ended: ${won ? 'WON' : 'LOST'} in ${turns} turns`)
    
    // AGC: Play win/lose sound
    if (won) {
      playSoundSafe('/sounds/youwin.mp3', 0.6)
    } else {
      playSoundSafe('/sounds/gameover.mp3', 0.6)
    }
    
    setBattleResult({ won, isDraw })
    setShowResultModal(true)
    setIsBattling(false)
    setShowBattle(false)
  }

  // AGC: If battle is active, show the battle component
  if (showBattle && isBattling) {
    const hybridContent = !wsSocket || !isConnected ? (
      <div className="flex min-h-[60vh] w-full items-center justify-center px-4">
        <div className="flex w-full max-w-md flex-col items-center gap-4 rounded-3xl bg-slate-900/80 px-6 py-10 text-center text-white shadow-2xl">
          <div className="h-16 w-16 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
          <p className="text-lg font-semibold">Connecting to backend…</p>
          <p className="text-sm text-slate-300">Simulation mode (real-time)</p>
        </div>
      </div>
    ) : (
      <HybridPvEBattle
        battleId={battleId}
        playerAddress={playerAddress}
        botLevel={botLevel}
        socket={wsSocket}
        selectedPotion={selectedPotion}
        selectedBuffs={selectedBuffs}
        onBattleEnd={handleBattleEnd}
        onBackToMenu={() => {
          setShowBattle(false)
          setIsBattling(false)
        }}
        simulationMode={true} // AGC: Enable simulation mode - disable auto-submit entirely
      />
    )

    return (
      <div className="w-full">{hybridContent}</div>
    )
  }

  // AGC: Top padding style matching Profile.tsx - 72px to clear TopNav (60px) + spacing
  const sharedTopPaddingStyle = { paddingTop: 'calc(env(safe-area-inset-top, 0px) + 72px)' }

  // AGC: Calculate XP progress for display
  const xpCurrent = playerStats?.experience ?? 0
  const xpToNext = playerStats?.experienceToNext ?? 0
  const xpTarget = xpCurrent + Math.max(0, xpToNext)
  const xpProgress = xpTarget > 0 ? Math.min(1, xpCurrent / xpTarget) : 0
  const attack = playerStats?.attack ?? 0
  const defense = playerStats?.defense ?? 0
  const walletShort = playerAddress ? `${playerAddress.slice(0, 4)}…${playerAddress.slice(-4)}` : 'Loading...'
  const playerLevel = playerStats?.level ?? 1

  // AGC: Note: playerAddress is always provided (fallback in MainMenu), so no loading state needed

  // AGC: Handle helper icon click - start battle tour from step 16
  const handleHelpClick = () => {
    // AGC: Start battle tour directly from step 16 (index 15)
    // AGC: The startTour function now accepts initialStepIndex parameter
    startTour('battle', 15) // AGC: Step 16 is index 15
  }

  return (
    <main
      className="pb-[88px] px-3 text-white flex flex-col gap-1.5"
      style={sharedTopPaddingStyle}
    >
      {/* AGC: Helper icon in top right - blinking animation */}
      <button
        onClick={handleHelpClick}
        className="fixed top-20 right-4 z-50 p-2 rounded-full bg-slate-800/80 hover:bg-slate-700/80 border border-slate-600 hover:border-cyan-500/50 transition-all shadow-lg animate-pulse"
        title="Start Simulation Tour"
        aria-label="Start Simulation Tour"
        style={{ marginTop: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}
      >
        <img 
          src={`${import.meta.env.BASE_URL || '/'}help.png`}
          alt="Help" 
          className="w-6 h-6 animate-pulse"
          onError={(e) => {
            // AGC: Fallback to HelpCircle icon if image not found
            const parent = e.currentTarget.parentElement
            if (parent) {
              e.currentTarget.style.display = 'none'
              const icon = document.createElement('div')
              icon.className = 'w-6 h-6 text-cyan-400 animate-pulse'
              icon.innerHTML = '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>'
              parent.appendChild(icon)
            }
          }}
        />
      </button>

      {/* AGC: Flashy green info panel at the top */}
      <div 
        data-tour="simulation-info-panel"
        className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg p-3 mb-2 flex items-center gap-2 shadow-lg border-2 border-green-400"
      >
        <Info className="w-5 h-5 text-white flex-shrink-0" />
        <p className="text-white font-semibold text-xs">
          Test the battle engine without any blockchain interaction, no connection to your wallet
        </p>
      </div>

      {/* AGC: Hero Header (No Card) - Following Profile.tsx pattern exactly */}
      <section data-tour="simulation-player-energy" className="pb-2 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500">SIMULATION BATTLE</p>
            <h1 className="text-2xl font-bold leading-tight text-white">Lv {playerLevel}</h1>
            <p className="text-[11px] text-slate-400">{walletShort} • ATK {attack} • DEF {defense}</p>
          </div>
          <div className="text-[10px] px-2.5 py-1 rounded-lg bg-white/5 border border-white/10">
            No Cost
          </div>
        </div>
        <div className="mt-3">
          <div className="flex items-center justify-between text-[10px] text-slate-400 mb-1">
            <span>XP Progress</span>
            <span>{xpCurrent.toLocaleString()} / {xpTarget.toLocaleString()}</span>
          </div>
          <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden shadow-inner shadow-black/30">
            <div
              className="h-full rounded-full bg-gradient-to-r from-blue-500 via-cyan-400 to-emerald-400 transition-all duration-500"
              style={{ width: `${xpProgress * 100}%` }}
            />
          </div>
        </div>
      </section>

      {/* AGC: Divider before Difficulty Selector */}
      <div className="border-b border-white/10"></div>

      {/* AGC: Difficulty Selector - Using reusable component */}
      <DifficultySelector
        selectedDifficulty={difficulty}
        onDifficultySelect={handleDifficultySelect}
        disabled={isBattling}
        variant="pve"
        battleCosts={{
          entryFee: 'No Cost',
          buffNFTsBurned: selectedBuffs.length || 0,
          winnerReceives: 'No Rewards',
          drawRefund: 'No Refund',
          xpAwarded: 'No XP (simulation)',
        }}
      />

      {/* AGC: Divider before Buff Selector */}
      <div className="border-b border-white/10"></div>

      {/* AGC: Buff Selector - Reusable component with simulation mode */}
      {/* AGC: Potions are now inside BuffSelector's POTIONS tab - no separate component needed */}
      <div data-tour="simulation-buff-area">
        <BuffSelector
          playerAddress={playerAddress}
          onBuffsSelected={handleBuffSelection}
          maxBuffs={2}
          disabled={isBattling}
          variant="compact"
          simulationMode={true}
          allowBothBuffsAndPotions={true} // AGC: Simulation allows both buffs and potions
        />
      </div>

      <div className="mt-2">
        <button
          ref={startBattleButtonRef}
          data-tour="simulation-start-button"
          onClick={handleStartBattle}
          disabled={isBattling || selectedBuffs.length === 0}
          className={`w-full rounded-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 disabled:from-gray-600 disabled:to-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition-all shadow-lg disabled:cursor-not-allowed text-sm uppercase tracking-wide ${
            highlightStartButton ? 'ring-2 ring-amber-400/70 ring-offset-2 ring-offset-slate-950' : ''
          }`}
        >
          {isBattling ? 'Battling…' : selectedBuffs.length === 0 ? '⚠️ Select at least one buff' : 'Start Simulation Battle'}
        </button>
      </div>

      {selectedBuffs.length === 0 && (
        <div className="text-center text-[11px] font-semibold text-amber-300 pb-2 border-b border-white/10">
          ⚠️ Please select at least one buff to start.
        </div>
      )}

      {/* AGC: Battle Result Modal */}
      <BattleResultModal
        isOpen={showResultModal}
        won={battleResult.won}
        isDraw={battleResult.isDraw}
        onClose={() => {
          setShowResultModal(false)
          setBattleId('')
          setSelectedBuffs([])
          hasBuffSelectionInteractionRef.current = false
          hasDifficultyInteractionRef.current = false
        }}
        autoCloseDelay={5000}
      />

      {/* AGC: Modal components */}
      {Modals}
    </main>
  )
}

